import { useMemo } from 'react';

import { useIssuesMetadata, useSelectedIssueTypes } from 'src/views/Admin/Modals/EditRuleModal/RuleForm/components/RuleFormContext';

import { generateJiraFieldUpdateActions } from './jiraActionsGenerator';
import * as actionJiraChangeStatus from './JiraChangeStatus';
import * as actionPdAcknowledge from './PdAcknowledge';
import * as actionPdAddDetails from './PdAddDetails';
import * as actionPdAddResponders from './PdAddResponders';
import * as actionPdResolve from './PdResolve';
import * as actionPdSetPriority from './PdSetPriority';
import * as actionPdSetTitle from './PdSetTitle';
import * as actionPdSetUrgency from './PdSetUrgency';
import * as actionPdTriggerIncidentWorkflow from './PdTriggerIncidentWorkflow';

export const useAllActions = () => {
  const { allFields } = useIssuesMetadata();
  return useMemo(() => [
    actionJiraChangeStatus,
    actionPdAcknowledge,
    actionPdAddResponders,
    actionPdResolve,
    actionPdTriggerIncidentWorkflow,
    actionPdSetPriority,
    actionPdSetUrgency,
    actionPdAddDetails,
    actionPdSetTitle,
    ...generateJiraFieldUpdateActions(allFields)
  ], [allFields]);
};

const actionsToOption = (actions) => actions.map(action => {
  let icon = undefined;
  if (action.type.includes('issue')) icon = 'jira';
  if (action.type.includes('incident')) icon = 'pd';
  if (action.type.includes('unsupported_jira_field')) icon = 'cross';

  return ({
    key: action.name,
    label: action.label,
    icon,
    isDisabled: icon === 'cross',
  });
});

export const useApplicableActions = ({ issueCreateActions, issueUpdateActions, incidentCreateActions, incidentUpdateActions } = {}) => {
  const types = useSelectedIssueTypes();
  const allActions = useAllActions();

  return useMemo(() => {
    const issueUnsupportedActions = issueCreateActions || issueUpdateActions;
    const allowedIds = (types || []).map(t => t.id);
    const applicableActions = allActions.filter(a => !Array.isArray(a.restrictToIssueTypes) || a.restrictToIssueTypes.some(x => allowedIds.includes(x)));

    const pdOptions = allActions.filter(a => (incidentCreateActions && a.type.includes('incident_create')) || (incidentUpdateActions && a.type.includes('incident_update')));
    const jiraOptions = applicableActions.filter(a => (issueCreateActions && a.type.includes('issue_create')) || (issueUpdateActions && a.type.includes('issue_update')));
    const jiraUnsupportedOptions = applicableActions.filter(a => issueUnsupportedActions && a.type === 'unsupported_jira_field');

    const result = [];

    if (jiraOptions.length) {
      result.push({
        label: 'Issue actions',
        options: actionsToOption(jiraOptions),
      });
    }

    if (pdOptions.length) {
      result.push({
        label: 'Incident actions',
        options: actionsToOption(pdOptions),
      });
    }

    if (jiraUnsupportedOptions.length) {
      result.push({
        label: 'unsupported fields',
        options: actionsToOption(jiraUnsupportedOptions)
      });
    }

    if (result.length === 1) {
      return result[0].options;
    } else {
      return result;
    }
  }, [issueCreateActions, issueUpdateActions, types, allActions, incidentCreateActions, incidentUpdateActions]);
};
